import {
    EFamilyAccountType,
    maxNameLength,
    ERoutes,
    ELinks,
    noNameAvailable,
    ELDFlags,
    EMixpanelEvent,
} from '../util/constants';
import language from '../util/language';
import { useHistory, useLocation } from 'react-router';
import API from '../api/api';
import {
    TCompleteConfirmAccountPost,
    TCreateAccountPost,
    TRegister3rdPartyPost,
    TRequestVerifyPost,
} from '../api/types';
import GlobalContext from './GlobalContext';
import { matchName } from '../util/helpers';
import { TBusiness } from '../util/types';
import { TConfirmAccountRouterState } from './ConfirmAccountContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react';
import mixpanel from 'mixpanel-browser';

export type TCreateFamilyAccountContext = {
    familyAccountType: EFamilyAccountType;
    createFamilyAccountUiState: ECreateFamilyAccountUiState;
    primaryUser: TFirstPartyUser | TThirdPartyUser;
    setPrimaryUser: (user: TFirstPartyUser | TThirdPartyUser) => void;
    originalUser: TUser;
    familyItems: TFamilyItem[];
    addFamilyItem: () => void;
    deleteFamilyItem: (index: number) => void;
    updateFamilyItemName: (nameType: ENameType, updateIndex: number, newName: string) => void;
    confirmFamilyAccount: () => void;
    confirmMigrationFamilyAccount: () => void;
    confirmConfirmationFamilyAccount: () => void;
    selectLinkedMigrationUser: (index: number) => void;
    confirmLinkedUser: () => void;
    selectLinkedConfirmationUser: (index: number) => void;
    linkedMigrationFamily: TMigrationFamily;
    linkedConfirmationFamily: TConfirmationFamily;
    submitFamilyAccount: () => Promise<void>;
    redirectUrl: string;
    errorMessage: string;
    linkingToken: string;
    tosAgreed: boolean;
    tosIsValid: boolean;
    setTosAgreed: (tosAgreed: boolean) => void;
    marketingOptIn: boolean;
    setMarketingOptIn: (marketingOptIn: boolean) => void;
    disableCreateFirstPartyAccount: boolean;
    submitVerifyPasscode: (passcode: string) => Promise<void>;
    resendVerifyPasscode: () => Promise<void>;
};

export type TFirstPartyAccount = {
    familyAccountType: EFamilyAccountType.firstParty;
    primaryUser: TFirstPartyUser;
};

export type TThirdParyAccount = {
    familyAccountType: EFamilyAccountType.thirdParty;
    primaryUser: TThirdPartyUser;
    accessToken: string;
};

export type TConfirmationAccount = {
    familyAccountType: EFamilyAccountType.confirmAccount;
    primaryUser: TFirstPartyUser & { id: string };
    confirmingUser: { firstName: string; lastName: string };
    code: string;
    unconfirmedLinkId: string;
};

export type TFirstPartyUser = {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
    country: string;
    password: string;
};

export type TThirdPartyUser = {
    canShowUsername: boolean;
    firstName: string;
    lastName: string;
    country: string;
};

export type TUserType = TFirstPartyUser | TThirdPartyUser;

export type TFamilyAccountType = TFirstPartyAccount | TThirdParyAccount | TConfirmationAccount;

export type TCreateFamilyAccountRouterState = TFamilyAccountType;

export type TFamilyItem = {
    firstName: string;
    lastName: string;
    firstNameIsValid: boolean;
    lastNameIsValid: boolean;
    firstNameValidationMessage: string;
    lastNameValidationMessage: string;
    hasFocus: boolean;
};

export type TUser = {
    firstName: string;
    lastName: string;
};

export type TMigrationDependentUser = TUser & { linkingToken?: string };

export type TConfirmationDependentUser = TUser & { unconfirmedLinkId?: string };

export type TMigrationFamily = {
    linkingToken?: string;
    dependentUsers: TMigrationDependentUser[];
};

export type TConfirmationFamily = {
    unconfirmedLinkId?: string;
    dependentUsers: TConfirmationDependentUser[];
};

export enum ECreateFamilyAccountUiState {
    default,
    ready,
    firstPartySuccess,
    thirdPartySuccess,
    migrationSuccess,
    confirmAccountSuccess,
    error,
    notInitialized,
}

export enum ENameType {
    first,
    last,
}

export enum EFamilyLinkPropertyName {
    migrateFamily = 'linkingToken',
    confirmFamily = 'unconfirmedLinkId',
}

const defaultPrimaryUser: TFirstPartyUser = {
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    password: '',
};

export const defaultLinkedMigrationFamily: TMigrationFamily = { dependentUsers: [] };

export const defaultLinkedConfirmationFamily: TConfirmationFamily = { dependentUsers: [] };

const CreateFamilyAccountContext = createContext<TCreateFamilyAccountContext | undefined>(undefined);

const newFamilyItem: TFamilyItem = {
    firstName: '',
    lastName: '',
    firstNameIsValid: true,
    lastNameIsValid: true,
    firstNameValidationMessage: '',
    lastNameValidationMessage: '',
    hasFocus: true,
};

export type TAutoLinkUserData = {
    primaryUser: TUser;
    originalUser: TUser;
    dependentUsers: TUser[];
    linkingTokenName: EFamilyLinkPropertyName;
    linkingTokenValue: string;
};

export type TManualLinkUserData = {
    dependentUsers: TUser[];
    linkingTokenName: EFamilyLinkPropertyName;
    linkingTokenValue: string;
    linkedUserIndex: number | null;
};

export const autoLinkFamilyUser = ({
    originalUser,
    primaryUser,
    dependentUsers,
    linkingTokenName,
    linkingTokenValue,
}: TAutoLinkUserData): TMigrationFamily | TConfirmationFamily | false => {
    let linkedFamily: TMigrationFamily | TConfirmationFamily;
    let numMatchingUsers = 0;
    // link primary user
    if (matchName(primaryUser.firstName, originalUser.firstName)) {
        numMatchingUsers = numMatchingUsers + 1;
        linkedFamily = {
            dependentUsers,
            [linkingTokenName]: linkingTokenValue,
        };
    }
    // link dependent users
    linkedFamily = {
        ...linkedFamily,
        dependentUsers: dependentUsers.map((dependentUser) => {
            if (matchName(dependentUser.firstName, originalUser.firstName)) {
                numMatchingUsers = numMatchingUsers + 1;
                return {
                    [linkingTokenName]: linkingTokenValue,
                    firstName: dependentUser.firstName,
                    lastName: dependentUser.lastName,
                };
            }
            return dependentUser;
        }),
    };
    return numMatchingUsers === 1 ? linkedFamily : false;
};

export const manualLinkFamilyUser = ({
    dependentUsers,
    linkingTokenName,
    linkingTokenValue,
    linkedUserIndex,
}: TManualLinkUserData): TMigrationFamily | TConfirmationFamily => {
    if (linkedUserIndex === null) {
        return defaultLinkedMigrationFamily;
    }
    if (linkedUserIndex === -1) {
        return {
            dependentUsers,
            [linkingTokenName]: linkingTokenValue,
        };
    }
    const linkedDependentUsers = dependentUsers.map((user, index) =>
        index === linkedUserIndex ? { ...user, [linkingTokenName]: linkingTokenValue } : { ...user },
    );
    return { dependentUsers: linkedDependentUsers };
};

export const CreateFamilyAccountProvider: FC = (props) => {
    const { subscriberId, coBranding, gatewayAPIBaseUrl, csrfToken } = useContext(GlobalContext);
    const history = useHistory();
    const { state: routerState }: { state: TCreateFamilyAccountRouterState } = useLocation();
    const [familyAccountType, setFamilyAccountType] = useState(EFamilyAccountType.none);
    const [primaryUser, setPrimaryUser] = useState<TUserType>(defaultPrimaryUser);
    const [accessToken, setAccessToken] = useState('');
    const [linkingToken, setLinkingToken] = useState('');
    const [unconfirmedLinkId, setUnconfirmedLinkId] = useState('');
    const [confirmAccountCode, setConfirmAccountCode] = useState('');
    const [originalUser, setOriginalUser] = useState<TUser>({ firstName: '', lastName: '' });
    const [createFamilyAccountUiState, setCreateFamilyAccountUiState] = useState(ECreateFamilyAccountUiState.default);
    const [familyItems, setFamilyItems] = useState<TFamilyItem[]>([newFamilyItem]);
    const [linkedMigrationFamily, setLinkedMigrationFamily] = useState<TMigrationFamily>(defaultLinkedMigrationFamily);
    const [linkedConfirmationFamily, setLinkedConfirmationFamily] = useState<TConfirmationFamily>(
        defaultLinkedConfirmationFamily,
    );
    const [redirectUrl, setRedirectUrl] = useState('');
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);
    const [tosAgreed, setTosAgreed] = useState(false);
    const [tosIsValid, setTosIsValid] = useState(true);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [disableCreateFirstPartyAccount, setDisableCreateFirstPartyAccount] = useState(true);
    const { [ELDFlags.useConsumerPasscodeVerification]: usePasscodeForVerifiication } = useFlags();
    const dependentUsers = useMemo(
        () => familyItems.map((item) => ({ firstName: item.firstName, lastName: item.lastName })),
        [familyItems],
    );
    const parseRouterState = () => {
        try {
            if (routerState.familyAccountType) {
                setFamilyAccountType(routerState.familyAccountType);
                if (routerState.primaryUser) {
                    setPrimaryUser(routerState.primaryUser);
                }
                if (routerState.familyAccountType === EFamilyAccountType.thirdParty) {
                    setAccessToken(routerState.accessToken);
                }
                if (routerState.familyAccountType === EFamilyAccountType.confirmAccount) {
                    setConfirmAccountCode(routerState.code);
                    setUnconfirmedLinkId(routerState.unconfirmedLinkId);
                    setOriginalUser(routerState.confirmingUser);
                }
                setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.ready);
            } else {
                setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.notInitialized);
            }
        } catch {
            setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.notInitialized);
        }
    };

    const addFamilyItem = () => {
        setFamilyItems([...familyItems, newFamilyItem]);
    };

    const deleteFamilyItem = (deleteIndex: number) => {
        setFamilyItems(familyItems.filter((item, itemIndex) => itemIndex !== deleteIndex));
    };

    const updateFamilyItemName = (nameType: ENameType, updateIndex: number, newName: string) => {
        const updatedFamily = familyItems.map((item, itemIndex) => {
            if (itemIndex === updateIndex) {
                if (nameType === ENameType.first) {
                    return {
                        ...item,
                        firstName: newName,
                    };
                }
                if (nameType === ENameType.last) {
                    return {
                        ...item,
                        lastName: newName,
                    };
                }
            }
            return item;
        });
        setFamilyItems(updatedFamily);
    };

    const prepopulateFirstDependentUser = () => {
        setFamilyItems([
            {
                ...newFamilyItem,
                firstName: `${primaryUser.firstName === originalUser.firstName ? '' : originalUser.firstName}`,
                lastName: originalUser.lastName,
            },
        ]);
    };

    const validateFamilyForm = (): boolean => {
        let formIsValid = true;
        const updatedFamily = familyItems.map((item, itemIndex) => {
            let firstNameIsValid = true;
            let firstNameValidationMessage = '';
            let lastNameIsValid = true;
            let lastNameValidationMessage = '';
            if (item.firstName.length < 1) {
                firstNameIsValid = false;
                firstNameValidationMessage = language.validation.firstName;
                formIsValid = false;
            }
            if (item.firstName.length > maxNameLength) {
                firstNameIsValid = false;
                firstNameValidationMessage = language.validation.firstNameMaxLength;
                formIsValid = false;
            }
            if (item.lastName.length < 1) {
                lastNameIsValid = false;
                lastNameValidationMessage = language.validation.lastName;
                formIsValid = false;
            }
            if (item.lastName.length > maxNameLength) {
                lastNameIsValid = false;
                lastNameValidationMessage = language.validation.lastNameMaxLength;
                formIsValid = false;
            }
            return {
                ...item,
                firstNameIsValid,
                firstNameValidationMessage,
                lastNameIsValid,
                lastNameValidationMessage,
            };
        });
        setFamilyItems(updatedFamily);

        return formIsValid;
    };

    const confirmFamilyAccount = () => {
        if (validateFamilyForm()) {
            history.push(ERoutes.createFamilyAccountConfirm);
        }
    };

    const confirmMigrationFamilyAccount = () => {
        if (validateFamilyForm()) {
            const user = primaryUser as TFirstPartyUser;
            const linkedMigrationFamily = autoLinkFamilyUser({
                originalUser,
                dependentUsers,
                linkingTokenName: EFamilyLinkPropertyName.migrateFamily,
                linkingTokenValue: linkingToken,
                primaryUser: { firstName: user.firstName, lastName: user.lastName },
            });
            if (linkedMigrationFamily) {
                setLinkedMigrationFamily(linkedMigrationFamily);

                history.push(ERoutes.createFamilyAccountConfirm);
            } else {
                history.push(ERoutes.createFamilyAccountManualLinkUser);
            }
        }
    };

    const confirmConfirmationFamilyAccount = () => {
        if (validateFamilyForm()) {
            const user = primaryUser as TFirstPartyUser;
            const linkedConfirmationFamily = autoLinkFamilyUser({
                originalUser,
                dependentUsers,
                linkingTokenName: EFamilyLinkPropertyName.confirmFamily,
                linkingTokenValue: unconfirmedLinkId,

                primaryUser: { firstName: user.firstName, lastName: user.lastName },
            });
            if (linkedConfirmationFamily) {
                setLinkedConfirmationFamily(linkedConfirmationFamily);
                history.push(ERoutes.createFamilyAccountConfirm);
            } else {
                history.push(ERoutes.createFamilyAccountManualLinkUser);
            }
        }
    };

    const selectLinkedMigrationUser = (index: number | null) => {
        const linkedMigrationFamily = manualLinkFamilyUser({
            dependentUsers,
            linkingTokenName: EFamilyLinkPropertyName.migrateFamily,
            linkingTokenValue: linkingToken,
            linkedUserIndex: index,
        });
        setLinkedMigrationFamily(linkedMigrationFamily);
    };

    const selectLinkedConfirmationUser = (index: number | null) => {
        const linkedConfirmationFamily = manualLinkFamilyUser({
            dependentUsers,
            linkingTokenName: EFamilyLinkPropertyName.confirmFamily,
            linkingTokenValue: unconfirmedLinkId,
            linkedUserIndex: index,
        });
        setLinkedConfirmationFamily(linkedConfirmationFamily);
    };

    const confirmLinkedUser = () => {
        history.push(ERoutes.createFamilyAccountConfirm);
    };

    const submitFamilyAccount = async () => {
        switch (familyAccountType) {
            case EFamilyAccountType.firstParty:
                await submitFirstPartyAccount();
                break;
            case EFamilyAccountType.thirdParty:
                await submitThirdPartyAccount();
                break;
            case EFamilyAccountType.confirmAccount:
                await submitConfirmAccount();
                break;
            default:
                setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.error);
                console.error('Family account type set incorrectly');
        }
    };

    const submitFirstPartyAccount = async () => {
        if (!tosAgreed) {
            setTosIsValid(false);
            throw Error('Has not agreed to terms of service', { cause: 'invalid' });
        }
        const user = primaryUser as TFirstPartyUser;
        const postObject: TCreateAccountPost = {
            dependentUsers,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            password: user.password,
            countryCode: user.country,
            marketingOptIn: marketingOptIn,
            ...(subscriberId && { subscriberId }),
            ...(usePasscodeForVerifiication && { usePasscode: true }),
        };
        try {
            const response = await API.post.createAccount(gatewayAPIBaseUrl, postObject);
            const { id } = response.data;
            setPrimaryUser({ ...primaryUser, id: id });
            setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.firstPartySuccess);
            mixpanel.identify(id);
        } catch (error) {
            setErrorMessage(language.validation.genericError);
            if (error.response && error.response.status === 409) {
                setErrorMessage(language.validation.accountExists.header);
            }
            setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.error);
        }
    };

    const submitThirdPartyAccount = async () => {
        const user = primaryUser as TThirdPartyUser;
        const postObject: TRegister3rdPartyPost = {
            dependentUsers,
            givenName: user.firstName ? user.firstName : noNameAvailable,
            surname: user.lastName ? user.lastName : noNameAvailable,
            countryCode: user.country,
        };
        try {
            const response = await API.post.register3rdParty(postObject, accessToken, csrfToken);
            const redirectUrl = response.data.redirectUrl ? response.data.redirectUrl : ELinks.userPortal;
            setRedirectUrl(redirectUrl);
            setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.thirdPartySuccess);
        } catch {
            setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.error);
        }
    };

    const submitConfirmAccount = async () => {
        const user = primaryUser as TFirstPartyUser;
        const postObject: TCompleteConfirmAccountPost = {
            code: confirmAccountCode,
            firstName: user.firstName,
            lastName: user.lastName,
            password: user.password,
            countryCode: user.country,
            ...linkedConfirmationFamily,
        };
        try {
            const response = await API.post.completeConfirmAccount(user.id, postObject);
            setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.confirmAccountSuccess);
            const { userBusinesses, legacyManualSyncToken } = response.data;
            const formattedBusinesses: TBusiness[] = userBusinesses.map((business) => ({
                users: business.users,
                name: business.subscriberName,
                city: business.city,
                stateOrProvince: business.stateOrProvinceCode,
                countryCode: business.countryCode,
                subscriberId: business.subscriberId,
            }));
            const routerState: TConfirmAccountRouterState = {
                confirmFamilyData: {
                    primaryUser: {
                        id: user.id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                    },
                    businesses: formattedBusinesses,
                    legacySyncToken: legacyManualSyncToken,
                },
            };
            history.push({ pathname: ERoutes.confirmAccountSuccess, state: routerState });
        } catch (error) {
            setCreateFamilyAccountUiState(ECreateFamilyAccountUiState.error);
            console.error('Failed to confirm user');
        }
    };

    const submitVerifyPasscode = async (passcode: string) => {
        const user = primaryUser as TFirstPartyUser;
        const queryParams = `?code=${passcode}&subscriberId=${subscriberId}&userId=${user.id}`;
        try {
            await API.get.verifyAccount(queryParams);
            mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'consumer_create_account' });
        } catch (error) {
            console.error('Failed to verify passcode');
            throw error.response?.status;
        }
    };

    const resendVerifyPasscode = async () => {
        const user = primaryUser as TFirstPartyUser;
        const postObject: TRequestVerifyPost = {
            email: user.email,
            userId: user.id,
            usePasscode: true,
            ...(subscriberId && { subscriberId }),
        };
        try {
            await API.post.requestVerify(gatewayAPIBaseUrl, postObject);
        } catch (error) {
            console.error('Failed to resend passcode');
            throw error.response?.status;
        }
    };

    useEffect(() => {
        if (familyAccountType === EFamilyAccountType.firstParty && !tosAgreed) {
            setDisableCreateFirstPartyAccount(true);
        } else {
            setDisableCreateFirstPartyAccount(false);
        }
    }, [familyAccountType, tosAgreed]);

    useEffect(() => {
        parseRouterState();
    }, []);

    useEffect(() => {
        if (coBranding.isDefaultFamilyAccount) {
            createFamilyAccountUiState === ECreateFamilyAccountUiState.ready && prepopulateFirstDependentUser();
        }
    }, [createFamilyAccountUiState]);

    return (
        <CreateFamilyAccountContext.Provider
            value={{
                familyAccountType,
                createFamilyAccountUiState,
                primaryUser,
                setPrimaryUser,
                originalUser,
                familyItems,
                addFamilyItem,
                deleteFamilyItem,
                updateFamilyItemName,
                confirmFamilyAccount,
                confirmMigrationFamilyAccount,
                confirmConfirmationFamilyAccount,
                selectLinkedMigrationUser,
                confirmLinkedUser,
                selectLinkedConfirmationUser,
                linkedMigrationFamily,
                linkedConfirmationFamily,
                submitFamilyAccount,
                submitVerifyPasscode,
                resendVerifyPasscode,
                redirectUrl,
                errorMessage,
                linkingToken,
                tosAgreed,
                setTosAgreed,
                tosIsValid,
                marketingOptIn,
                setMarketingOptIn,
                disableCreateFirstPartyAccount,
            }}
            {...props}
        >
            {props.children}
        </CreateFamilyAccountContext.Provider>
    );
};

export default CreateFamilyAccountContext;
